import { Injectable, Injector } from "@angular/core";
import { SystemNotificationService } from "ng/src/app/notifications/system-notification.service";
import { MessageService } from "primeng/api";
import { ServiceWorkerService } from "service-worker/service-worker.service";
import { PersistentDialogService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";
import { SystemNotification, SystemNotificationAttentionId } from "ng/src/app/notifications/notification.model";
import { filter, map, switchMap, take } from "rxjs/operators";
import { of } from "rxjs";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { PersistentDialogController } from "ng/src/app/shared/persistent-dialog/persistent-dialog.model";
import { AlertDialogComponent } from "ng/src/app/shared/persistent-dialog/alert-dialog.component";
import { TrustHtmlPipe } from "ng/src/app/shared/trust-html.pipe";
import { Dialog } from "primeng/dialog";
import { SystemNotificationMessage } from "ng/src/app/hybrid-helpers/p-toast-wrapper.component";
import { ILocationService } from "angular";
import IInjectorService = angular.auto.IInjectorService;

@Injectable({
	providedIn: 'root'
})
export class SystemNotificationNotifierService {
	
	private lastDelivered?: Date;
	private trustHtml: TrustHtmlPipe;
	
	private get $location() {
		return (this.injector.get('$injector') as IInjectorService).get('$location') as ILocationService;
	}
	
	constructor(
		private systemNotificationService: SystemNotificationService,
		private serviceWorkerService: ServiceWorkerService,
		private messageService: MessageService,
		private dialogService: PersistentDialogService,
		private injector: Injector
	) {
		this.trustHtml = Injector.create({
			providers: [
				{ provide: TrustHtmlPipe }
			],
			parent: injector
		})
			.get(TrustHtmlPipe);
		
		systemNotificationService.getPoll()
			.pipe(
				switchMap((n) => {
					if (!AppStateService.currentUser) {
						return AppStateService.currentUser$.pipe(
							filter(user => !!user),
							take(1),
							map(user => {
								if (user) return n;
								return [];
							})
						);
					}
					
					return of(n);
				})
			)
			.pipe(
				switchMap((n) => {
					if (Array.isArray(n) && n.length > 0) {
						return this.process(n);
					}
					
					return of();
				})
			)
			.subscribe();
	}
	
	private process(notifications: SystemNotification[]) {
		const lastDelivered = this.lastDelivered ?? this.systemNotificationService.getLastDeliveredTimestamp();
		
		const filtered = notifications.filter(n => lastDelivered ? new Date(n.created) > lastDelivered : true);
		filtered.forEach(n => {
			switch (n.attention.id) {
				case SystemNotificationAttentionId.STICKY:
				case SystemNotificationAttentionId.BANNER:
					this.sendNotification(n);
					break;
				case SystemNotificationAttentionId.ALERT:
					this.sendAlert(n);
					break;
			}
		});
		
		if (filtered.length > 0) {
			const n = filtered[0];
			const ob$ = this.systemNotificationService.updateLastDelivered(n);
			this.lastDelivered = new Date(n.created);
			return ob$;
		}
		else {
			return of();
		}
	}
	
	private sendNotification(notification: SystemNotification) {
		const message: SystemNotificationMessage = {
			severity: 'info',
			key: 'system-toast',
			summary: notification.title,
			life: 15000,
			sticky: notification.attention.id === SystemNotificationAttentionId.STICKY,
			notification,
			action: () => this.$location.path('/notifications')
				.search('notification', notification.id)
				.search('tab', 'system')
		};
		
		this.messageService.add(message);
	}
	
	private sendAlert(notification: SystemNotification) {
		let dialog: Dialog | undefined;
		this.dialogService.next(new PersistentDialogController(AlertDialogComponent)
			.setOnDialogCreated(d => {
				d.header = notification.title;
				d.style = { width: '750px', height: '1000px'};
				d.modal = true;
				d.draggable = false;
				d.resizable = false;
				dialog = d;
			})
			.setOnComponentCreated(c => {
				c.instance.message = this.trustHtml.transform(notification.content);
			})
		);
	}
}